import logger from '@eventbrite/client-logger';
import { ProgressIndicator } from '@eventbrite/eds-progress-indicator';
import { getWindowObject } from '@eventbrite/feature-detection';
import { GenericLazyString } from '@eventbrite/i18n';
import {
    AuthorizationData,
    ButtonColorOption,
    ButtonLabelOption,
    ButtonShapeOption,
    ButtonSizeOption,
    ButtonStyle,
    Environment,
    FlowType,
    Intent,
    LineItem,
    LineItemKind,
} from '@eventbrite/paypal-checkout-types';
import { PayPalCheckoutCreatePaymentOptions } from 'braintree-web';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import type { ExpressCheckoutSubmitDataType } from '../../../../models/expressCheckout';
import { StatsigValueType } from '../../../../models/expressCheckout';
import { PaypalBuyerDetails } from '../../../../models/paymentMethods/paypal';
import { Ticket } from '../../../../models/tickets';
import { PaypalScriptStatus } from '../../../../state/paypal';
import { getPaypalCheckoutInstance } from '../../../../utils/paypalScriptLoader';
import { checkEnablePayPalLineItemsFeatureGate } from '../../../../utils/statsig';
import './PaypalButton.scss';

const getPayPalSdk = ():
    | {
          Buttons: {
              driver: (
                  framework: string,
                  config: any,
              ) => typeof React.Component;
          };
      }
    | undefined => getWindowObject('paypal' as keyof Window) as any;

enum PaypalLayout {
    vertical = 'vertical',
    horizontal = 'horizontal',
}

interface PaypalButtonStyle extends ButtonStyle {
    height: number;
    layout: PaypalLayout;
}

const PAYPAL_BUTTON_HEIGHT_IN_PIXELS = 44;

// PayPal Checkout Button documentation can be checked in: https://developer.paypal.com/docs/checkout/how-to/customize-button/
const getPaypalButtonStyle = (color: ButtonColorOption): PaypalButtonStyle => ({
    color,
    label: ButtonLabelOption.PayPal,
    shape: ButtonShapeOption.Rect,
    size: ButtonSizeOption.Responsive,
    tagline: false,
    layout: PaypalLayout.horizontal,
    height: PAYPAL_BUTTON_HEIGHT_IN_PIXELS,
});

export interface PaypalButtonNonCallbackProps {
    amountTotal: string;
    amountCurrency: string;
    color?: ButtonColorOption;
    currencyFormat: string;
    breakdownItems: Array<{
        id: string;
        majorValue: number | null;
        content: GenericLazyString | string;
    }>;
    env: Environment;
    handleSubmit?: (props: ExpressCheckoutSubmitDataType) => void;
    isCheckoutButton?: boolean;
    isDisabled: boolean;
    paypalScriptStatus: PaypalScriptStatus;
    selectedTickets: Array<Ticket>;
}

export interface PaypalButtonCallbackProps {
    onPaypalAuthorize: (buyerDetails: PaypalBuyerDetails) => void;
    onPaypalCancel?: () => void;
    onPaypalClick: (data: { fundingSource: string }) => void;
    onPaypalError: (error: Error) => void;
    onPaypalInit?: () => void;
}

export interface PaypalButtonProps
    extends PaypalButtonNonCallbackProps,
        PaypalButtonCallbackProps {}

export interface PayPalTicketVariant {
    cost?: { value: number };
    name: string;
    selectedQuantity: number;
}

export const PaypalButton: React.FunctionComponent<PaypalButtonProps> = ({
    amountCurrency,
    amountTotal,
    color = ButtonColorOption.Blue,
    breakdownItems,
    env,
    handleSubmit,
    isCheckoutButton = false,
    isDisabled = true,
    onPaypalAuthorize,
    onPaypalCancel = () => undefined,
    onPaypalClick,
    onPaypalError,
    onPaypalInit,
    paypalScriptStatus,
    selectedTickets,
}) => {
    const [buttonActions, setButtonActions] = useState<{
        enable: Function;
        disable: Function;
    }>();

    useEffect(() => {
        if (buttonActions) {
            if (isDisabled) {
                buttonActions.disable();
            } else {
                buttonActions.enable();
            }
        }
    }, [buttonActions, isDisabled]);

    // /!\ WARNING _createPaypalPayment MUST return a string: this string is the orderId used by Paypal internally
    const createPaypalPayment: () => Promise<string> = async () => {
        const checkoutInstance = await getPaypalCheckoutInstance();
        if (!checkoutInstance) {
            throw new Error(
                '[EB-152450] Paypal checkout instance creation failed',
            );
        }

        const payPalSelectedTickets = selectedTickets
            .flatMap(
                (ticket) =>
                    (!ticket.selectedQuantity
                        ? ticket.variants
                        : ticket) as PayPalTicketVariant,
            )
            .filter((ticket) => ticket.selectedQuantity > 0)
            .map(
                (ticket): LineItem => ({
                    quantity: ticket.selectedQuantity.toString(),
                    unitAmount: `${ticket.cost ? ticket.cost.value / 100 : 0}`,
                    name: ticket.name.substring(0, 127),
                    kind: LineItemKind.Debit,
                    unitTaxAmount: undefined,
                    description: undefined,
                    productCode: undefined,
                    url: undefined,
                }),
            );

        const payPalBreakdownItems = breakdownItems.flatMap((item) =>
            item.id !== 'subtotal' && item.majorValue
                ? [
                      {
                          unitAmount: `${
                              item.majorValue ? item.majorValue / 100 : ''
                          }`,
                          quantity: '1',
                          name: item.content.toString(),
                          kind: LineItemKind.Debit,
                          unitTaxAmount: undefined,
                          description: undefined,
                          productCode: undefined,
                          url: undefined,
                      },
                  ]
                : [],
        );

        const baseCreatePaymentOptions: PayPalCheckoutCreatePaymentOptions = {
            flow: FlowType.Checkout,
            amount: amountTotal,
            currency: amountCurrency,
            intent: Intent.Authorize,
        };

        const createPaymentOptions: PayPalCheckoutCreatePaymentOptions =
            checkEnablePayPalLineItemsFeatureGate()
                ? {
                      ...baseCreatePaymentOptions,
                      lineItems: [
                          ...payPalSelectedTickets,
                          ...payPalBreakdownItems,
                      ],
                  }
                : baseCreatePaymentOptions;

        return checkoutInstance.createPayment(createPaymentOptions);
    };

    const onAuthorizeHandler = async (data: AuthorizationData) => {
        const checkoutInstance = await getPaypalCheckoutInstance();

        if (!checkoutInstance) {
            throw new Error(
                '[EB-152450] Paypal checkout instance creation failed',
            );
        }

        const {
            nonce,
            details: { creditFinancingOffered, email, firstName, lastName },
        } = await checkoutInstance.tokenizePayment(data);

        // Save the nonce, the user's email and the settings of credit in redux.
        onPaypalAuthorize({
            credit: creditFinancingOffered !== undefined,
            email,
            firstName,
            lastName,
            nonce,
        });
    };

    const onPaypalClickHandler = (data: any) => {
        if (handleSubmit) {
            handleSubmit({
                isPayPal: true,
                statsigValue: StatsigValueType.PAYPAL,
            });
        }

        onPaypalClick(data);
    };

    const onPaypalErrorHandler = (error: Error) => {
        logger.error('[EB-152450] Paypal button raised an error', {
            error,
        });
        onPaypalError(error);
    };

    const onPaypalInitHandler = (_data: any, actions: any) => {
        if (onPaypalInit) {
            onPaypalInit();
        }

        setButtonActions(actions);
    };

    const paypalButtonClassName = classNames('react-paypal-button', {
        'react-paypal-button--modal-footer': isCheckoutButton,
    });

    const paypalWrapperClassName = classNames({
        'react-paypal-wrapper': isCheckoutButton,
    });

    const payPalSdk = getPayPalSdk();
    if (!payPalSdk || paypalScriptStatus !== PaypalScriptStatus.loaded) {
        return (
            <ProgressIndicator
                data-spec="paypal-button-loading-indicator"
                style="gradient"
                size="small-chunky"
                shape="circular"
            />
        );
    }

    try {
        const PaypalButtonComponent = payPalSdk.Buttons.driver('react', {
            React,
            ReactDOM,
        });

        return (
            <div className={paypalWrapperClassName}>
                <div
                    className={paypalButtonClassName}
                    data-spec="react-paypal-button"
                >
                    <PaypalButtonComponent
                        commit={false}
                        createOrder={createPaypalPayment}
                        data-spec="paypal-button-component"
                        env={env}
                        onApprove={onAuthorizeHandler}
                        onCancel={onPaypalCancel}
                        onClick={onPaypalClickHandler}
                        onError={onPaypalErrorHandler}
                        onInit={onPaypalInitHandler}
                        style={getPaypalButtonStyle(color)}
                    />
                </div>
            </div>
        );
    } catch (e) {
        logger.error(e);
        return null;
    }
};
